
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { ApplicationNav } from '@/components/application/ApplicationNav';
import { cn } from '@/lib/utils';
import type { Application } from '@/types/database.types';

interface CollapsibleSidebarProps {
  currentSection: number;
  onSectionChange: (index: number) => void;
  data: Partial<Application>;
  className?: string;
}

export const CollapsibleSidebar = ({
  currentSection,
  onSectionChange,
  data,
  className
}: CollapsibleSidebarProps) => {
  const [isCollapsed, setIsCollapsed] = useState(false);

  return (
    <div className={cn('relative transition-all duration-300 border-r border-border', 
         isCollapsed ? 'w-16' : 'w-64',
         className)}>
      <div className={cn(
        'h-full transition-all duration-300 py-4',
        isCollapsed ? 'opacity-0 invisible' : 'opacity-100 visible'
      )}>
        <ApplicationNav
          currentSection={currentSection}
          onSectionChange={onSectionChange}
          data={data}
        />
      </div>
      
      <Button
        variant="ghost"
        size="icon"
        className="absolute -right-3 top-3 rounded-full border bg-background shadow-sm hover:bg-muted"
        onClick={() => setIsCollapsed(!isCollapsed)}
        aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
      >
        {isCollapsed ? 
          <ChevronRight className="h-4 w-4" /> : 
          <ChevronLeft className="h-4 w-4" />}
      </Button>
      
      {isCollapsed && (
        <div className="flex flex-col items-center space-y-4 py-4">
          {Array.from({ length: 8 }).map((_, index) => (
            <Button
              key={index}
              variant={currentSection === index ? "default" : "ghost"}
              size="icon"
              className="rounded-full w-8 h-8"
              onClick={() => onSectionChange(index)}
              aria-label={`Go to section ${index + 1}`}
            >
              {index + 1}
            </Button>
          ))}
        </div>
      )}
    </div>
  );
};
